import { useState, useEffect } from 'react';
import { SignJWT } from 'jose';
import Sidebar from './components/Sidebar';
import Dashboard from './components/Dashboard';
import Projects from './components/Projects';
import Repositories from './components/Repositories';
import Controls from './components/Controls';
import SecurityEvents from './components/SecurityEvents';
import SecurityAlerts from './components/SecurityAlerts';
import AlertRules from './components/AlertRules';
import SecurityTimeline from './components/SecurityTimeline';
import RiskAcceptances from './components/RiskAcceptances';
import ComplianceFrameworks from './components/ComplianceFrameworks';
import ActivityFeed from './components/ActivityFeed';
import Settings from './components/Settings';
import Tools from './components/Tools';
import Remediations from './components/Remediations';
import Interpreters from './components/Interpreters';
import Normalizers from './components/Normalizers';
import Tags from './components/Tags';
import Evidence from './components/Evidence';
import Conformance from './components/Conformance';
import apiClient from './lib/api-client';

// Placeholder components for different views
function PlaceholderView({ title, description }: { title: string; description: string }) {
  return (
    <div className="flex flex-col items-center justify-center h-96 text-center">
      <div className="mb-4 text-ink/40">
        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/>
          <path d="M12 16v-4"/>
          <path d="M12 8h.01"/>
        </svg>
      </div>
      <h2 className="font-display font-bold text-ink text-[24px] mb-2">{title}</h2>
      <p className="font-display text-[16px] text-ink/60 max-w-md">
        {description}
      </p>
      <div className="mt-6 px-4 py-2 bg-primary-50 text-primary-600 rounded-lg font-display text-[14px] font-medium">
        Coming soon in the unified dashboard
      </div>
    </div>
  );
}

function LoginView({ onLogin }: { onLogin: (token: string) => void }) {
  const [loading, setLoading] = useState(false);
  
  // Create valid JWT for authentication
  const handleLogin = async () => {
    setLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    try {
      const secret = new TextEncoder().encode('your-jwt-secret-key-change-in-production');
      
      const payload = {
        sub: '1',
        email: 'demo@cylift.dev',
        name: 'Demo User',
        role: 'admin'
      };
      
      // Create properly signed JWT using jose library
      const token = await new SignJWT(payload)
        .setProtectedHeader({ alg: 'HS256' })
        .setIssuedAt()
        .setExpirationTime('1h')
        .sign(secret);
      
      const authContext = {
        token,
        user: {
          id: '1',
          email: 'demo@cylift.dev',
          name: 'Demo User',
          role: 'admin' as const,
          permissions: ['read', 'write', 'delete', 'admin'],
        },
        expires: Math.floor(Date.now() / 1000) + 3600 // 1 hour from now
      };
      
      apiClient.setAuth(authContext);
      setLoading(false);
      onLogin(token);
    } catch (error) {
      console.error('Login failed:', error);
      setLoading(false);
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="bg-white rounded-[20px] shadow-[0_8px_30px_rgb(0,0,0,0.12)] p-8 w-full max-w-md">
        <div className="text-center mb-8">
          <div className="h-12 w-12 rounded-[8px] bg-ink grid place-items-center mx-auto mb-4">
            <span className="font-display font-extrabold text-white text-[18px] tracking-tight">C</span>
          </div>
          <h1 className="font-display font-bold text-ink text-[28px] tracking-tight">
            Welcome to Cylift<span className="text-primary-500">.Dev</span>
          </h1>
          <p className="font-display text-[16px] text-ink/60 mt-2">
            Unified security and compliance dashboard
          </p>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block font-display text-[14px] font-semibold text-ink mb-2">
              Email
            </label>
            <input
              type="email"
              defaultValue="demo@cylift.dev"
              disabled
              className="w-full px-4 py-3 rounded-[10px] border border-gray-200 bg-gray-50 font-display text-[14px] text-ink/60"
            />
          </div>
          
          <div>
            <label className="block font-display text-[14px] font-semibold text-ink mb-2">
              Password
            </label>
            <input
              type="password"
              defaultValue="••••••••"
              disabled
              className="w-full px-4 py-3 rounded-[10px] border border-gray-200 bg-gray-50 font-display text-[14px] text-ink/60"
            />
          </div>
          
          <button
            onClick={handleLogin}
            disabled={loading}
            className="w-full bg-primary-500 text-white font-display font-bold text-[16px] py-3 rounded-[10px] hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="4"/>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                </svg>
                Signing in...
              </>
            ) : (
              'Sign In (Demo)'
            )}
          </button>
        </div>

        <div className="mt-6 text-center">
          <p className="font-display text-[12px] text-ink/40">
            Demo version • No real authentication required
          </p>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  const [currentRoute, setCurrentRoute] = useState('dashboard');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = () => {
      setIsAuthenticated(apiClient.isAuthenticated());
      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
    setCurrentRoute('dashboard');
  };

  const navigate = (route: string) => {
    if (route === 'login') {
      setIsAuthenticated(false);
      apiClient.clearAuth();
      return;
    }
    setCurrentRoute(route);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex items-center gap-2 text-ink/60">
          <svg className="animate-spin h-6 w-6" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" fill="none" stroke="currentColor" strokeWidth="4"/>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
          </svg>
          <span className="font-display text-[16px]">Loading...</span>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <LoginView onLogin={handleLogin} />;
  }

  const renderCurrentView = () => {
    switch (currentRoute) {
      case 'dashboard':
        return <Dashboard />;
      
      case 'security-events':
        return <SecurityEvents />;
        
      case 'security-alerts':
        return <SecurityAlerts />;
      
      case 'alert-rules':
        return <AlertRules />;
        
      case 'security-timeline':
        return <SecurityTimeline />;
      
      case 'repositories':
        return <Repositories navigate={navigate} />;
        
      case 'compliance-projects':
      case 'projects':
        return <Projects navigate={navigate} />;
        
      case 'compliance-frameworks':
      case 'frameworks':
        return <ComplianceFrameworks />;
        
      case 'compliance-controls':
      case 'controls':
        return <Controls navigate={navigate} />;
        
      case 'evidence':
        return <Evidence data={{}} navigate={navigate} />;
        
      case 'conformance':
        return <Conformance data={{}} navigate={navigate} />;
        
      case 'risk-acceptances':
        return <RiskAcceptances />;
        
      case 'interpreters':
        return <Interpreters data={{}} navigate={navigate} />;
        
      case 'normalizers':
        return <Normalizers data={{}} />;
        
      case 'tools':
        return <Tools />;
        
      case 'remediations':
        return <Remediations data={{}} navigate={navigate} />;
        
      case 'tags':
        return <Tags data={{}} navigate={navigate} />;
        
      case 'insights':
        return <PlaceholderView 
          title="Cross-Domain Insights" 
          description="AI-powered insights correlating security events with compliance status." 
        />;
        
      case 'activity':
        return <ActivityFeed />;
        
      case 'settings':
        return <Settings />;
        
      default:
        return <Dashboard />;
    }
  };

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar route={currentRoute} navigate={navigate} />
      
      <main className="flex-1 overflow-y-auto">
        <div className="p-8">
          {renderCurrentView()}
        </div>
      </main>
    </div>
  );
}